import type {
  DataGridRef,
  DialogRef,
  DropDownResult,
  DynamicFieldComponentProps,
  OdinKey,
  RadioGroupResult,
} from '@myosh/odin-components';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Observable, skipWhile, Subject, take, takeUntil } from 'rxjs';
import type { CloneRecordsApiProps, RecordResult } from '../../../@types/records';
import { useAppDispatch } from '../../../redux/hooks';
import { submitCloneRecordsJob } from '../../../redux/slices/background-job-slice';
import { getDataGridReference } from '../../../services/data-grid.service';
import { showInfo } from '../../../services/notification.service';
import { forceAssert, isEmptyObject } from '../../../utils/type-guards';
import ConfirmationDialog from '../../common/confirmation-dialog.component';
import WrapperStyledButtonField from '../../common/wrapper-styled-button-field';
import { RecordSaveState } from '../../record/record.component';
import type { CloneWizardRef } from './clone-record-wizard.component';
import CloneRecordWizzard from './clone-record-wizard.component';

export interface CloneRecordButtonFieldProps extends DynamicFieldComponentProps<unknown> {
  id?: OdinKey;
  buttonCaption?: string;
  recordId?: number;
  cloneRecordBackgroundJobItemText?: string;
  formId: number;
  saveRecord?: () => Observable<RecordResult>;
  recordSaveState?: RecordSaveState;
  shouldUseCloneWizzard?: boolean;
  cloneWizardTargetPersonField?: number;
  cloneWizardTargetWorkflowStep?: number;
  allowUserAttachmentCloning?: boolean;
  moduleFormCloneTargetId?: number;
}

export interface CloneWizardState {
  status?: DropDownResult;
  personField?: DropDownResult;
  users: DropDownResult[];
  groups: DropDownResult[];
  attachmentOption?: RadioGroupResult;
}

export const initialCloneWizardState = {
  users: [],
  groups: [],
  attachmentOption: { text: 'Yes', value: true },
  status: undefined,
  personField: undefined,
};

function CloneRecordButtonField({
  id,
  buttonCaption,
  recordId,
  cloneRecordBackgroundJobItemText,
  readOnly,
  formId,
  saveRecord,
  recordSaveState,
  shouldUseCloneWizzard,
  cloneWizardTargetPersonField,
  cloneWizardTargetWorkflowStep,
  allowUserAttachmentCloning,
  moduleFormCloneTargetId,
}: CloneRecordButtonFieldProps) {
  const [cloneWizardState, setCloneWizardState] = useState<CloneWizardState>(initialCloneWizardState);
  const isNewRecord = recordSaveState === RecordSaveState.New;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const destroySubject = useRef<Subject<void>>(new Subject<void>());
  const gridRef = useRef<DataGridRef | undefined>(undefined);
  const cloneRecordAttachmentConfirmationDialogRef = useRef<DialogRef>(null);
  const saveConfirmationDialogRef = useRef<DialogRef>(null);
  const newRecordIdRef = useRef<number | undefined>(undefined);
  const cloneRecordWizardRef = useRef<CloneWizardRef>(null);

  useEffect(() => {
    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((dataGridRef) => {
        if (dataGridRef) {
          gridRef.current = dataGridRef;
        }
      });

    return () => {
      destroySubject.current?.next();
      destroySubject.current?.complete();
    };
  }, []);

  const onCloneRecord = (newRecordId?: number) => {
    if (allowUserAttachmentCloning && !shouldUseCloneWizzard) {
      showCloneRecordAttachmentConfirmationDialog();
    } else {
      cloneRecordsAsync(false, newRecordId);
    }
  };

  const promptOnRecordCloneButtonClick = () => {
    if (isNewRecord) {
      onShowSaveConfirmationDialog();
    } else {
      onCloneRecord();
    }
  };

  const onShowSaveConfirmationDialog = () => {
    saveConfirmationDialogRef.current?.show();
  };

  const onSaveConfirm = () => {
    const resultObservable = saveRecord?.();
    if (resultObservable) {
      resultObservable
        .pipe(
          skipWhile((result) => isEmptyObject(forceAssert<Record<string, unknown>>(result))),
          take(1),
          takeUntil(destroySubject.current)
        )
        .subscribe((record: RecordResult) => {
          if (record.id) {
            newRecordIdRef.current = record.id;

            if (shouldUseCloneWizzard) {
              cloneRecordWizardRef.current?.open();
            } else {
              onCloneRecord(record.id);
            }
          }
        });
    }
  };

  const showCloneRecordAttachmentConfirmationDialog = () => {
    cloneRecordAttachmentConfirmationDialogRef.current?.show();
  };

  const onConfirmCloneRecordAttachments = () => {
    cloneRecordsAsync(true, newRecordIdRef.current);
  };

  const onCloseCloneRecordAttachmentConfirmationDialog = (returnValue?: string) => {
    if (returnValue !== 'save') {
      cloneRecordsAsync(false, newRecordIdRef.current);
    }
  };

  const cloneRecordsAsync = (cloneAttachment?: boolean, newRecordId?: number) => {
    let recordCloneJobApiProps: CloneRecordsApiProps = {
      recordId: newRecordId ?? recordId ?? 0,
      fieldId: id as number,
    };
    if (!shouldUseCloneWizzard && allowUserAttachmentCloning) {
      recordCloneJobApiProps = { ...recordCloneJobApiProps, cloneAttachment };
    } else if (shouldUseCloneWizzard) {
      const listOfUserIds = cloneWizardState.users.map((item) => {
        return item.value as number;
      });
      const listOfGroupIds = cloneWizardState.groups.map((item) => {
        return item.value as number;
      });
      recordCloneJobApiProps = {
        ...recordCloneJobApiProps,
        targetPersonFieldId: cloneWizardTargetPersonField ?? (cloneWizardState.personField?.value as number) ?? 0,
        targetWorkflowStepId: cloneWizardTargetWorkflowStep ?? (cloneWizardState.status?.value as number) ?? 0,
        userIds: listOfUserIds,
        groupIds: listOfGroupIds,
      };
      if (allowUserAttachmentCloning) {
        recordCloneJobApiProps = {
          ...recordCloneJobApiProps,
          cloneAttachment: (cloneWizardState.attachmentOption?.value as boolean) ?? true,
        };
      }
    }

    dispatch(
      submitCloneRecordsJob({
        ...recordCloneJobApiProps,
        details: {
          text: cloneRecordBackgroundJobItemText?.includes('-')
            ? cloneRecordBackgroundJobItemText
            : cloneRecordBackgroundJobItemText
              ? `${cloneRecordBackgroundJobItemText} - ${newRecordId}`
              : `${newRecordId}`,
          hasMultipleRecords: shouldUseCloneWizzard,
        },
      })
    );

    showInfo(t('performing-cloning-message'));
  };

  return (
    <>
      {shouldUseCloneWizzard ? (
        <CloneRecordWizzard
          ref={cloneRecordWizardRef}
          disabled={readOnly}
          buttonCaption={buttonCaption}
          onClick={promptOnRecordCloneButtonClick}
          formId={formId}
          cloneWizardTargetPersonField={cloneWizardTargetPersonField}
          cloneWizardTargetWorkflowStep={cloneWizardTargetWorkflowStep}
          allowUserAttachmentCloning={allowUserAttachmentCloning}
          moduleFormCloneTargetId={moduleFormCloneTargetId}
          cloneWizardState={cloneWizardState}
          setCloneWizardState={setCloneWizardState}
          showSaveConfirmationDialog={onShowSaveConfirmationDialog}
          isNewRecord={isNewRecord}
        />
      ) : (
        <WrapperStyledButtonField
          onClick={promptOnRecordCloneButtonClick}
          disabled={readOnly}
          buttonCaption={buttonCaption}
          icon="Copy"
        />
      )}
      <ConfirmationDialog ref={saveConfirmationDialogRef} onConfirm={onSaveConfirm} hasCancelButton />
      <ConfirmationDialog
        ref={cloneRecordAttachmentConfirmationDialogRef}
        header={t('attachments')}
        confirmButtonText={t('yes')}
        onConfirm={onConfirmCloneRecordAttachments}
        cancelButtonText={t('no')}
        onClose={onCloseCloneRecordAttachmentConfirmationDialog}
        hasCancelButton
      >
        <p>{t('clone-records-attachments-label')}</p>
      </ConfirmationDialog>
    </>
  );
}

export default CloneRecordButtonField;
